import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { TrendingUp } from "lucide-react";

const COLORS = ["#D9FF26", "#00F0FF", "#a78bfa", "#f472b6", "#fbbf24", "#34d399", "#fb923c", "#60a5fa"];

function Panel({ title, children }) {
  return <div className="bg-card border border-border rounded-lg p-6"><h3 className="font-heading text-lg font-semibold mb-5">{title}</h3>{children}</div>;
}

export default function Analytics() {
  const [a, setA] = useState(null);
  useEffect(() => { api.get("/analytics").then(({ data }) => setA(data)).catch(() => {}); }, []);
  const empty = a && a.by_source.length === 0 && a.over_time.length === 0;

  return (
    <div data-testid="analytics-page">
      <div className="mb-8"><h4 className="text-xs font-semibold tracking-[0.25em] uppercase text-zinc-500 mb-2">Intelligence</h4><h1 className="font-heading text-4xl font-black tracking-tighter uppercase">Analytics</h1></div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="bg-card border border-border rounded-lg p-6"><div className="font-mono text-4xl font-semibold text-primary">${(a?.total_revenue || 0).toLocaleString()}</div><div className="text-xs uppercase tracking-widest text-zinc-500 mt-1">Total Revenue</div></div>
        <div className="bg-card border border-border rounded-lg p-6"><div className="font-mono text-4xl font-semibold">${(a?.total_payouts || 0).toLocaleString()}</div><div className="text-xs uppercase tracking-widest text-zinc-500 mt-1">Total Payouts</div></div>
      </div>

      {empty ? (
        <div className="bg-card border border-border rounded-lg text-center py-20"><TrendingUp className="h-10 w-10 text-zinc-700 mx-auto mb-4" /><p className="text-zinc-500">Log royalties to see revenue analytics.</p></div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Panel title="Revenue Over Time">
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={a?.over_time || []}>
                <defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#D9FF26" stopOpacity={0.5} /><stop offset="100%" stopColor="#D9FF26" stopOpacity={0} /></linearGradient></defs>
                <CartesianGrid stroke="#27272a" vertical={false} /><XAxis dataKey="period" stroke="#71717a" fontSize={11} /><YAxis stroke="#71717a" fontSize={11} />
                <Tooltip contentStyle={{ background: "#18181b", border: "1px solid #27272a", borderRadius: 8 }} />
                <Area type="monotone" dataKey="amount" stroke="#D9FF26" strokeWidth={2} fill="url(#g)" />
              </AreaChart>
            </ResponsiveContainer>
          </Panel>
          <Panel title="Revenue by Source">
            <ResponsiveContainer width="100%" height={240}>
              <PieChart><Pie data={a?.by_source || []} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} innerRadius={50}>{(a?.by_source || []).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Pie><Tooltip contentStyle={{ background: "#18181b", border: "1px solid #27272a", borderRadius: 8 }} /></PieChart>
            </ResponsiveContainer>
          </Panel>
          <Panel title="Top Earning Songs">
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={a?.top_songs || []} layout="vertical"><CartesianGrid stroke="#27272a" horizontal={false} /><XAxis type="number" stroke="#71717a" fontSize={11} /><YAxis type="category" dataKey="name" stroke="#71717a" fontSize={11} width={100} /><Tooltip contentStyle={{ background: "#18181b", border: "1px solid #27272a", borderRadius: 8 }} /><Bar dataKey="value" fill="#00F0FF" radius={[0, 4, 4, 0]} /></BarChart>
            </ResponsiveContainer>
          </Panel>
          <Panel title="Royalties by Territory">
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={a?.by_territory || []}><CartesianGrid stroke="#27272a" vertical={false} /><XAxis dataKey="name" stroke="#71717a" fontSize={11} /><YAxis stroke="#71717a" fontSize={11} /><Tooltip contentStyle={{ background: "#18181b", border: "1px solid #27272a", borderRadius: 8 }} /><Bar dataKey="value" fill="#D9FF26" radius={[4, 4, 0, 0]} /></BarChart>
            </ResponsiveContainer>
          </Panel>
        </div>
      )}
    </div>
  );
}
